import React, { useCallback, useMemo, useState } from "react";
import { IconButton, Tooltip, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridEditInputCell,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridRowOrderChangeParams,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { RATDataGrid } from "components/RATDataGrid";
import {
  DisplayUnitsDataGridProps,
  DisplayUnitsDataGridRow,
  ROUND_DIGIT_MAX,
  ROUND_DIGIT_MIN,
  RoundMethod,
} from "./UnitPickerInterfaces";
import {
  CreateDisplayUnitsDataGridRow,
  ValidateRoundDigits,
} from "./UnitPickerUtility";

const DisplayUnitsDataGrid: React.FC<DisplayUnitsDataGridProps> = ({
  rows,
  setRows,
  displayUnitOptions,
  setIsGridInEditMode,
}) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const syncEditModeFlag = useCallback(
    (model: GridRowModesModel) => {
      const isEditing = Object.values(model).some(
        (rowMode) => rowMode?.mode === GridRowModes.Edit
      );
      setIsGridInEditMode(isEditing);
    },
    [setIsGridInEditMode]
  );

  const handleRowModesModelChange = useCallback(
    (model: GridRowModesModel) => {
      setRowModesModel(model);
      syncEditModeFlag(model);
    },
    [syncEditModeFlag]
  );

  const handleAddRow = useCallback(() => {
    // Row reorder is disabled while a sort model is active.
    setSortModel([]);

    const newRow: DisplayUnitsDataGridRow = CreateDisplayUnitsDataGridRow("");
    setRows((prevRows) => [...prevRows, newRow]);
  }, [setRows]);

  const handleDeleteRow = useCallback(
    (id: GridRowId) => () => {
      setTimeout(() => {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id));
        setRowModesModel((prev) => {
          const next = { ...prev };
          delete next[id];
          syncEditModeFlag(next);
          return next;
        });
      });
    },
    [setRows, syncEditModeFlag]
  );

  const handleRowOrderChange = useCallback(
    (params: GridRowOrderChangeParams) => {
      setRows((prevRows) => {
        const updatedRows = [...prevRows];
        const [movedRow] = updatedRows.splice(params.oldIndex, 1);
        updatedRows.splice(params.targetIndex, 0, movedRow);
        return updatedRows;
      });
    },
    [setRows]
  );

  const handleApplySort = useCallback(() => {
    setRows((prevRows) => {
      const sortedRows = [...prevRows].sort((a, b) => {
        for (let i = 0; i < sortModel.length; i++) {
          const { field, sort } = sortModel[i];
          if (a[field] < b[field]) return sort === "asc" ? -1 : 1;
          if (a[field] > b[field]) return sort === "asc" ? 1 : -1;
        }
        return 0;
      });
      return sortedRows;
    });
    setSortModel([]);
  }, [setRows, sortModel]);

  const handleClearSort = useCallback(() => {
    setSortModel([]);
  }, []);

  const processRowUpdate = useCallback(
    (newRow: DisplayUnitsDataGridRow) => {
      const validatedRow: DisplayUnitsDataGridRow = {
        ...newRow,
        roundDigit: ValidateRoundDigits(newRow.roundDigit),
      };

      setRows((prevRows) => {
        if (validatedRow.isDefault) {
          return prevRows.map((row) =>
            row.id === validatedRow.id
              ? validatedRow
              : { ...row, isDefault: false }
          );
        }
        return prevRows.map((row) =>
          row.id === validatedRow.id ? validatedRow : row
        );
      });

      return validatedRow;
    },
    [setRows]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "action",
        headerName: "Actions",
        type: "actions",
        width: 75,
        headerAlign: "center",

        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key={`delete-${params.id}`}
            icon={<DeleteOutlineIcon color="primary" />}
            label="Delete"
            onClick={handleDeleteRow(params.id)}
          />,
        ],
      },
      {
        field: "displayUnit",
        headerName: "Display Unit",
        type: "singleSelect",
        valueOptions: displayUnitOptions,
        width: 150,
        editable: true,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "isDefault",
        headerName: "Default",
        type: "boolean",
        width: 70,
        editable: true,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "roundMethod",
        headerName: "Round Method",
        type: "singleSelect",
        valueOptions: [RoundMethod.DECIMAL, RoundMethod.SIGNIFICANT],
        width: 150,
        editable: true,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "roundDigit",
        headerName: "Round Digits",
        type: "number",
        width: 150,
        editable: true,
        headerAlign: "center",
        align: "center",
        renderEditCell: (params) => (
          <div style={{ display: "flex", alignItems: "center" }}>
            <GridEditInputCell
              {...params}
              inputProps={{
                max: ROUND_DIGIT_MAX,
                min: ROUND_DIGIT_MIN,
              }}
            />
            <IconButton
              aria-label="clear"
              size="small"
              onClick={() =>
                params.api.setEditCellValue({
                  id: params.id,
                  field: "roundDigit",
                  value: "",
                })
              }
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </div>
        ),
      },
    ],
    [displayUnitOptions, handleDeleteRow]
  );

  const toolbar = useMemo(
    () => ({
      handleAddRow,
      handleApplySort,
      handleClearSort,
      showColumnsButton: false,
      showFilterButton: false,
      showDensityButton: false,
    }),
    [handleAddRow, handleApplySort, handleClearSort]
  );

  return (
    <Tooltip
      title="Display Unit values will be used to populate the .DISPLAYUNIT subproperty.  These represent all of the units the property value can be converted to and displayed on the UI or Reports."
      placement="top-end"
      arrow
    >
      {/*
        Flex column parent replaces deprecated autoHeight: without min/max height,
        DataGrid sizes to its content (all rows visible). See MUI layout docs.
      */}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="caption" gutterBottom>
          Display Units
        </Typography>

        <RATDataGrid
          rows={rows}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => console.error(error)}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          disableColumnMenu
          rowReordering
          onRowOrderChange={handleRowOrderChange}
          toolbar={toolbar}
        />
      </div>
    </Tooltip>
  );
};

export default DisplayUnitsDataGrid;
